import classNames from "classnames";
import React, {
  Dispatch,
  SetStateAction,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { toast } from "react-toastify";
import { useDebounce } from "usehooks-ts";

import { LottieLoading } from "../../../../components/graphics/LottieLoading";
import { SettingsCheckbox } from "../../../../components/inputs/SettingsCheckbox";
import { Button } from "../../../../components/newUI/button";
import PlusSvg from "../../../../components/svg/PlusSvg";
import { SearchSvg } from "../../../../components/svg/SearchSvg";
import {
  StatusModel,
  SubStatusModel,
} from "../../../../factories/statuses/models/status.model";
import { useAddSubStatusMutation } from "../../../../slices/StatusesSlice";
import { UpdateWorkTypeStatusModel } from "../../../../slices/WorkTypeSlice";
import { CreateNewStatus } from "./CreateNewStatus";

const STATUS_LIST_OPEN_EVENT = "proc:work-type-status-list-open";

type Props = {
  subStatuses: SubStatusModel[];
  workTypeStatus: UpdateWorkTypeStatusModel | undefined;
  workTypeId?: string;
  setWorkTypeStatuses: Dispatch<SetStateAction<UpdateWorkTypeStatusModel[]>>;
  getStatuses: () => Promise<StatusModel[] | undefined>;
  isLoadingStatuses: boolean;
};

export const SelectStatusDropdown: React.FC<Props> = ({
  subStatuses,
  workTypeStatus,
  workTypeId,
  setWorkTypeStatuses,
  getStatuses,
  isLoadingStatuses,
}) => {
  const searchRef = useRef<HTMLInputElement>(null);
  const boxRef = useRef<HTMLDivElement>(null);

  const [isOpen, setIsOpen] = useState(false);
  const [search, setSearch] = useState("");
  const [isOpenCreateSubStatus, setIsOpenCreateSubStatus] = useState(false);
  const [draftSubStatusIds, setDraftSubStatusIds] = useState<string[]>(
    workTypeStatus?.workTypeSubStatusIds || [],
  );

  const debounceSearch = useDebounce(search, 300);

  const [addSubStatus, { isLoading: isLoadingAdd }] = useAddSubStatusMutation();

  const searchStatuses = useMemo(() => {
    return debounceSearch
      ? subStatuses.filter((item) =>
          item.subStatus.toLowerCase().includes(debounceSearch.toLowerCase()),
        )
      : subStatuses;
  }, [debounceSearch, subStatuses]);

  const selectedSubStatusIds = useMemo(
    () => workTypeStatus?.workTypeSubStatusIds || [],
    [workTypeStatus?.workTypeSubStatusIds],
  );

  const areSelectionsEqual = (first: string[], second: string[]) => {
    if (first.length !== second.length) {
      return false;
    }

    const firstSorted = [...first].sort();
    const secondSorted = [...second].sort();

    return firstSorted.every((item, index) => item === secondSorted[index]);
  };

  const onChangeDraft = (value: boolean, id: string) => {
    setDraftSubStatusIds((prev) => {
      if (value) {
        return prev.includes(id) ? prev : [...prev, id];
      }

      return prev.filter((item) => item !== id);
    });
  };

  const onAddSelectedStatuses = () => {
    setWorkTypeStatuses((prev) =>
      prev.map((item) =>
        item.workTypeStatusId === workTypeStatus?.workTypeStatusId
          ? {
              ...item,
              workTypeSubStatusIds: draftSubStatusIds,
            }
          : item,
      ),
    );
    setIsOpen(false);
  };

  const onCreateSubStatus = async (subStatusName: string) => {
    const trimmedSubStatusName = subStatusName.trim();

    if (!trimmedSubStatusName || !workTypeStatus?.workTypeStatusId) {
      return;
    }

    try {
      const createdSubStatus = await addSubStatus({
        subStatus: trimmedSubStatusName,
        workTypeStatusId: workTypeStatus?.workTypeStatusId,
        serviceIds: workTypeId ? [workTypeId] : [],
      }).unwrap();
      const createdSubStatusId = createdSubStatus.id;

      setWorkTypeStatuses((prev) =>
        prev.map((item) =>
          item.workTypeStatusId === workTypeStatus.workTypeStatusId
            ? {
                ...item,
                workTypeSubStatusIds: item.workTypeSubStatusIds.includes(
                  createdSubStatusId,
                )
                  ? item.workTypeSubStatusIds
                  : [...item.workTypeSubStatusIds, createdSubStatusId],
              }
            : item,
        ),
      );
      setDraftSubStatusIds((prev) =>
        prev.includes(createdSubStatusId)
          ? prev
          : [...prev, createdSubStatusId],
      );
      await getStatuses();
      toast("Status successfully created", {
        type: "success",
      });
      setIsOpenCreateSubStatus(false);
    } catch {
      toast("Something went wrong", {
        type: "error",
      });
    }
  };

  useEffect(() => {
    if (!isOpen) {
      setDraftSubStatusIds(selectedSubStatusIds);
    }
  }, [isOpen, workTypeStatus?.workTypeStatusId, selectedSubStatusIds]);

  // QA batch 16 (SET-028): only one Add Status list is open at a time and a
  // click outside closes it (two open lists overlapped, so "Create new
  // status" could be clicked in the wrong group's list).
  const ownId = workTypeStatus?.workTypeStatusId || "";
  useEffect(() => {
    if (!isOpen) return;
    window.dispatchEvent(
      new CustomEvent(STATUS_LIST_OPEN_EVENT, { detail: ownId }),
    );
    const onOtherOpen = (event: Event) => {
      if ((event as CustomEvent<string>).detail !== ownId) setIsOpen(false);
    };
    const onMouseDown = (event: MouseEvent) => {
      if (isOpenCreateSubStatus) return;
      if (boxRef.current?.contains(event.target as Node)) return;
      setIsOpen(false);
    };
    window.addEventListener(STATUS_LIST_OPEN_EVENT, onOtherOpen);
    document.addEventListener("mousedown", onMouseDown);
    return () => {
      window.removeEventListener(STATUS_LIST_OPEN_EVENT, onOtherOpen);
      document.removeEventListener("mousedown", onMouseDown);
    };
  }, [isOpen, ownId, isOpenCreateSubStatus]);

  return (
    <>
      <div className={"relative"} ref={boxRef}>
        <div className={"p-4"}>
          <Button
            type={"button"}
            variant={"outline"}
            fullWidth
            leftIcon={<PlusSvg />}
            onClick={() => setIsOpen((prev) => !prev)}
            className={
              "border-[#C8D2DE] px-4 font-semibold text-[#253747] hover:border-[#798CA4] hover:bg-[#4C637B0D]"
            }>
            Add Status
          </Button>
        </div>
        <div
          className={classNames(
            isOpen
              ? "absolute top-[calc(100%+8px)] w-full max-w-full rounded-lg border border-[#E4E9F2] bg-white"
              : "hidden",
          )}>
          <div className={"px-3 pb-1 pt-3"}>
            <div
              onClick={() => searchRef?.current?.focus()}
              className={
                "flex items-center gap-2 rounded-lg border border-[#C8D2DE] pl-2"
              }>
              <SearchSvg sizeCx={"w-4 h-4 text-[#A1AFC0]"} />
              <input
                ref={searchRef}
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                type={"text"}
                placeholder={"Search..."}
                className={
                  "h-9 w-full bg-transparent text-sm outline-none placeholder:text-[#A1AFC0]"
                }
              />
            </div>
          </div>
          <div className={"max-h-[230px] overflow-y-auto"}>
            {isLoadingStatuses ? (
              <LottieLoading />
            ) : (
              searchStatuses.map((sub) => (
                <div
                  key={sub.id}
                  className={
                    "grid h-10 grid-cols-[16px_1fr] items-center gap-1 pl-4 pr-1"
                  }>
                  <SettingsCheckbox
                    value={draftSubStatusIds.includes(sub.id)}
                    onChange={(value) => onChangeDraft(value, sub.id)}
                  />
                  <div
                    title={sub.subStatus}
                    className={"truncate text-sm font-semibold text-[#1D2C3B]"}>
                    {sub.subStatus}
                  </div>
                </div>
              ))
            )}
          </div>
          <div
            className={
              "flex items-center justify-between gap-2 rounded-b-lg border-t border-[#E4E9F2] bg-white p-3"
            }>
            <div
              onClick={() => setIsOpenCreateSubStatus(true)}
              className={
                "flex cursor-pointer items-center gap-2 text-sm font-semibold text-[#6938EF]"
              }>
              <PlusSvg />
              Create new status
            </div>
            <Button
              type={"button"}
              size={"sm"}
              autoWidth
              disabled={areSelectionsEqual(
                draftSubStatusIds,
                selectedSubStatusIds,
              )}
              onClick={onAddSelectedStatuses}
              className={"px-4"}>
              Done
            </Button>
          </div>
        </div>
      </div>
      {isOpenCreateSubStatus && (
        <CreateNewStatus
          onClose={() => setIsOpenCreateSubStatus(false)}
          onSubmit={onCreateSubStatus}
          isLoading={isLoadingAdd}
        />
      )}
    </>
  );
};
